import type { BadgeVariant } from '@/components/ui';
import type {
  AppealDecision,
  HrCaseAccessRow,
  DisciplineDecision,
  GrievanceFinding,
  HrCase,
  HrCaseEntry,
  HrCaseMemberRole,
  HrCaseStatus,
} from '@/api/services/hrCases.service';

export const LABEL_CLASS =
  'mb-2 block text-[11px] font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400';

export const CASE_STATUS: Record<HrCaseStatus, { label: string; variant: BadgeVariant }> = {
  open: { label: 'Open', variant: 'info' },
  investigating: { label: 'Investigating', variant: 'info' },
  hearing_scheduled: { label: 'Hearing scheduled', variant: 'warning' },
  decided: { label: 'Decided', variant: 'success' },
  appeal_requested: { label: 'Appeal requested', variant: 'warning' },
  appeal_decided: { label: 'Appeal decided', variant: 'success' },
  closed: { label: 'Closed', variant: 'neutral' },
  withdrawn: { label: 'Withdrawn', variant: 'neutral' },
  submitted: { label: 'Submitted', variant: 'info' },
  blocked: { label: 'Needs routing', variant: 'danger' },
  acknowledged: { label: 'Acknowledged', variant: 'info' },
  findings_issued: { label: 'Findings issued', variant: 'success' },
};

export const ROLE_LABELS: Record<HrCaseMemberRole, string> = {
  investigator: 'Investigator',
  decision_maker: 'Decision maker',
  appeal_officer: 'Appeal officer',
  handler: 'Handler',
  observer: 'Observer',
};

/** Roles a disciplinary case accepts, in the order they usually join. */
export const DISCIPLINE_ROLES: HrCaseMemberRole[] = [
  'investigator',
  'decision_maker',
  'appeal_officer',
  'observer',
];

/** Roles a grievance accepts, in the order they usually join. */
export const GRIEVANCE_ROLES: HrCaseMemberRole[] = [
  'handler',
  'investigator',
  'appeal_officer',
  'observer',
];

export const FINDING_LABELS: Record<GrievanceFinding, string> = {
  upheld: 'Upheld',
  partially_upheld: 'Partially upheld',
  not_upheld: 'Not upheld',
};

export const ROUTING_REASONS: Record<string, string> = {
  respondent_is_handler: 'the grievance names an intake handler',
  respondent_is_hr: 'the employee said it concerns HR',
  no_intake_handler: 'no intake handler was available',
};

export const DECISION_LABELS: Record<DisciplineDecision, string> = {
  no_action: 'No action',
  warning: 'Warning',
  dismissal: 'Dismissal',
};

export const APPEAL_LABELS: Record<AppealDecision, string> = {
  upheld: 'Upheld',
  varied: 'Varied',
  overturned: 'Overturned',
};

export const ENTRY_LABELS: Record<HrCaseEntry['type'], string> = {
  note: 'Note',
  evidence: 'Evidence',
  meeting: 'Meeting',
  finding: 'Finding',
  decision: 'Decision',
  appeal: 'Appeal',
  transition: 'Status change',
  member_change: 'Team change',
  party_response: 'Response',
};

export const TERMINAL_STATUSES: HrCaseStatus[] = ['closed', 'withdrawn'];

export function formatCaseDate(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

export function formatCaseDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function statusLabel(status: HrCaseStatus | null | undefined): string {
  return status ? (CASE_STATUS[status]?.label ?? status.replaceAll('_', ' ')) : '';
}

/** One readable line for an entry the record shows without a body. */
export function describeEntry(entry: HrCaseEntry): string | null {
  if (entry.type === 'transition') {
    return entry.fromStatus
      ? `${statusLabel(entry.fromStatus)} → ${statusLabel(entry.toStatus)}`
      : `Opened as ${statusLabel(entry.toStatus).toLowerCase()}`;
  }
  if (entry.type === 'member_change') {
    const role = entry.detail.role as HrCaseMemberRole | undefined;
    return entry.detail.change === 'added'
      ? `Added a ${role ? ROLE_LABELS[role].toLowerCase() : 'member'}`
      : 'Removed a team member';
  }
  if (entry.type === 'appeal' && typeof entry.detail.appealDecision === 'string') {
    return `Appeal ${APPEAL_LABELS[entry.detail.appealDecision as AppealDecision].toLowerCase()}`;
  }
  if (entry.detail.lifecycle === 'suspend') return 'Employee suspended';
  if (entry.detail.lifecycle === 'reinstate') return 'Suspension lifted';
  if (entry.type === 'finding' && typeof entry.detail.finding === 'string') {
    return `Finding: ${FINDING_LABELS[entry.detail.finding as GrievanceFinding].toLowerCase()}`;
  }
  if (entry.type === 'decision' && typeof entry.detail.decision === 'string') {
    const label = DECISION_LABELS[entry.detail.decision as DisciplineDecision];
    return entry.detail.warningLevelKey ? `${label}: ${String(entry.detail.warningLevelKey)}` : label;
  }
  return null;
}

/** Converts a label into a settings key the server accepts. */
export function keyFromLabel(label: string): string {
  const key = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return /^[a-z]/.test(key) ? key : `item_${key}`.slice(0, 40);
}

export function formatBytes(size: number): string {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}

/** Saves a downloaded file under its original name. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/** `yyyy-mm-ddThh:mm` for a `datetime-local` input, in the browser's zone. */
export function toDateTimeInput(value: Date): string {
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}T${pad(
    value.getHours()
  )}:${pad(value.getMinutes())}`;
}

/** Short sentence for the summary strip. */
export function warningLine(hrCase: HrCase): string | null {
  const warning = hrCase.outcome?.warning;
  if (!warning) return null;
  const level = hrCase.settings.warningLevels.find((item) => item.key === warning.levelKey);
  return `${level?.label ?? warning.levelKey} · ${warning.expiredAt ? 'expired' : 'expires'} ${formatCaseDate(warning.expiredAt ?? warning.expiresAt)}`;
}

/** Case commands as the sentence that follows a person's name. */
const COMMAND_TEXT: Record<string, string> = {
  open: 'opened the case',
  submit: 'submitted the case',
  submit_grievance: 'submitted a grievance',
  add_entry: 'added to the record',
  add_member: 'added someone to the case',
  remove_member: 'removed someone from the case',
  investigate: 'started the investigation',
  issue_findings: 'issued the findings',
  schedule_hearing: 'scheduled a hearing',
  party_response: 'recorded a response',
  decide: 'recorded the decision',
  issue_warning_letter: 'issued a warning letter',
  suspend: 'suspended the case',
  suspend_subject: 'suspended the employee',
  reinstate: 'resumed the case',
  reinstate_subject: 'reinstated the employee',
  separation: 'started a separation',
  cancel_separation: 'cancelled the separation',
  complete_action: 'completed an action',
  request_appeal: 'requested an appeal',
  decide_appeal: 'decided the appeal',
  acknowledge: 'acknowledged the outcome',
  withdraw: 'withdrew the case',
  close: 'closed the case',
  update_case_settings: 'changed the case settings',
};

/** What an access-log row records, in words. */
export function accessActionText(row: Pick<HrCaseAccessRow, 'action' | 'detail'>): string {
  if (row.action === 'command') {
    const command = String(row.detail.command ?? '');
    return COMMAND_TEXT[command] ?? (command ? `did “${command.replaceAll('_', ' ')}”` : 'changed the case');
  }
  if (row.action === 'attachment_download') return 'downloaded a file';
  if (row.action === 'view') return 'viewed the case';
  return 'exported the case';
}
